import React from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faUndoAlt } from '@fortawesome/pro-solid-svg-icons'
import { faCog } from '@fortawesome/pro-light-svg-icons'

import Button from 'components/Button'
import DigitalTemplateEnabledFeature from 'components/DigitalTemplateEnabledFeature'
import IconWithMenuExtended from 'components/IconWithMenuExtended'

import Link from 'suitTheme/Link'

import styles from './styles.scss'

const swalOptions = {
  text: 'Are you sure?',
  icon: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Yes, reset it.',
  confirmButtonColor: '#DD6B55',
}

const updateDataSource = (dataSource, props) => {
  const {
    dataSource: { id },
    dataSourceType,
    callbacks: { updateCampaignProposal, updateDigitalPage },
  } = props

  const updated = {
    id,
    ...dataSource,
  }

  const updateFn = dataSourceType === 'CampaignProposal' ? updateCampaignProposal : updateDigitalPage
  updateFn(updated)
}

const resetCustomImages = (props) => {
  const {
    dataSource: { id },
    dataSourceType,
    callbacks: { resetCampaignProposalCustomImages, resetDigitalPageCustomImages },
  } = props

  const updateFn = dataSourceType === 'CampaignProposal' ? resetCampaignProposalCustomImages : resetDigitalPageCustomImages

  updateFn({ id })
}

const confirmResetCustomData = (props) => {
  swal
    .fire({
      title: 'Reset to Default Text?',
      ...swalOptions,
    })
    .then(({ value }) => {
      if (value){
        const updatedDataSource = {
          data: null,
          custom_data: null,
        }

        updateDataSource(updatedDataSource, props)
      }
    })
}

const confirmResetCustomImages = (props) => {
  swal
    .fire({
      title: 'Reset to Default Images?',
      ...swalOptions,
    })
    .then(({ value }) => {
      if (value){
        resetCustomImages(props)
      }
    })
}

const confirmResetDisabledSections = (props) => {
  swal
    .fire({
      title: 'Reset Disabled Sections?',
      ...swalOptions,
    })
    .then(({ value }) => {
      if (value){
        const updatedDataSource = {
          disabled_sections: null,
        }

        updateDataSource(updatedDataSource, props)
      }
    })
}

const confirmResetSortedSections = (props) => {
  swal
    .fire({
      title: 'Reset Sorted Sections?',
      ...swalOptions,
    })
    .then(({ value }) => {
      if (value){
        const updatedDataSource = {
          sorted_section_keys: null,
        }

        updateDataSource(updatedDataSource, props)
      }
    })
}

const confirmResetCoverImage = (props) => {
  swal
    .fire({
      title: 'Reset Cover Image?',
      ...swalOptions,
    })
    .then(({ value }) => {
      if (value){
        const updatedDataSource = {
          cover_image_id: null,
        }

        updateDataSource(updatedDataSource, props)
      }
    })
}

const EditingNavBar = (props) => {
  const {
    callbacks: { downloadPdf },
    campaignProposal: { id, options },
    digitalTemplate,
    editing,
    redirect,
  } = props

  const goBackUrl = redirect || `/agency/campaign_proposals/manage#/proposals/${id}/edit/digitalProposal`

  if (!editing) return null

  return (
    <div className={styles.root}>
      <span className={styles.saveNotice}>Changes Saved Automatically</span>

      {downloadPdf && (
        <DigitalTemplateEnabledFeature digitalTemplate={digitalTemplate}>
          <Button
            ghost
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={downloadPdf}
            size="medium"
            style={{ marginRight: 10 }}
            text="Download PDF"
          />
        </DigitalTemplateEnabledFeature>
      )}

      <Button href={goBackUrl} size="medium" style={{ marginRight: 10 }} text="Go Back" />

      <IconWithMenuExtended
        icon={<FontAwesomeIcon icon={faCog} />}
        style={{ margin: '0 25px 0 10px', fontSize: 20 }}
        closeOnClick
      >
        <Link key="custom-data" onClick={() => confirmResetCustomData(props)}>
          <FontAwesomeIcon icon={faUndoAlt} className={styles.icon} /> Reset to Default Text
        </Link>

        <Link key="custom-images" onClick={() => confirmResetCustomImages(props)}>
          <FontAwesomeIcon icon={faUndoAlt} className={styles.icon} /> Reset to Default Images
        </Link>

        <Link key="disabled-sections" onClick={() => confirmResetDisabledSections(props)}>
          <FontAwesomeIcon icon={faUndoAlt} className={styles.icon} /> Reset Disabled Sections
        </Link>

        <Link key="sorted-sections" onClick={() => confirmResetSortedSections(props)}>
          <FontAwesomeIcon icon={faUndoAlt} className={styles.icon} /> Reset Sorted Sections
        </Link>

        {options && options.cover_image_id && (
          <Link key="cover-image" onClick={() => confirmResetCoverImage(props)}>
            <FontAwesomeIcon icon={faUndoAlt} className={styles.icon} /> Reset Cover Image
          </Link>
        )}

      </IconWithMenuExtended>
    </div>
  )
}

EditingNavBar.propTypes = {
  callbacks: PropTypes.shape({
    downloadPdf: PropTypes.func.isRequired,
    resetCampaignProposalCustomImages: PropTypes.func.isRequired,
    resetDigitalPageCustomImages: PropTypes.func.isRequired,
    updateCampaignProposal: PropTypes.func.isRequired,
    updateDigitalPage: PropTypes.func.isRequired,
  }),
  campaignProposal: PropTypes.object.isRequired,
  dataSource: PropTypes.object.isRequired,
  dataSourceType: PropTypes.string.isRequired,
  digitalTemplate: PropTypes.object,
  editing: PropTypes.bool,
  redirect: PropTypes.string,
}

EditingNavBar.defaultProps = {
  callbacks: {},
  digitalTemplate: {},
}

export default EditingNavBar
